import { insightsIntl } from "../strings/intl";
import { AppShell, Callout, FullWidthPage, Spinner, Tabs } from "@wayflyer/flyui";
import { DatePickerHeader, type DatePickerHeaderProps } from "@wayflyer/flyui-dataviz";
import * as React from "react";
import { useLoaderData } from "react-router";
import { useAnalyticsContext } from "shared-utils";

import { Notes, type NotesProps } from "../components/notes";
import { DataGroupContainer } from "../components/styled";
import { TabTitle } from "../components/tab-title";
import { type InsightsLoaderReturn } from "../loader";
import { getUrls } from "../urls";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { addNotes, getNotes } from "../utils/notes";
import { type UIElement } from "../utils/types";

import { Graphs } from "./graphs";
import {
  makeAdditionalReferenceContext,
  makeReferenceHeader,
  makeReferenceProps,
  useReferencePeriod,
} from "./reference";
import { addGroupedTiles, useGroupedTiles } from "./tiles";
import { ComparedToContainer, Container, LeftSpacer, SelectContainer } from "./ui/styled";
import { GroupedTiles, type GroupedTilesProps } from "./ui/tiles";

export interface TabProps {
  title: string;
  tooltip?: string;
  details?: string;
  groupedTiles?: GroupedTilesProps;
  graphs?: any;
  notes?: NotesProps;
}

const Tab = ({ title, tooltip, groupedTiles, notes, graphs }: TabProps): UIElement => {
  return (
    <>
      <TabTitle title={title} tooltip={tooltip} />
      {groupedTiles ? <GroupedTiles {...groupedTiles} /> : null}
      {graphs ? <Graphs {...graphs} /> : null}
      {notes ? <Notes {...notes} /> : null}
    </>
  );
};

export type SortConfig = {
  name: string;
  initialValue: string;
  label?: string;
  prefix?: string;
  options: any[];
  onChange: (item: any | null) => void;
};

interface HeaderProps {
  title: string;
  comparedTo: string;
  datePicker: DatePickerHeaderProps;
  referenceComboProps: SortConfig;
  referenceContext?: string | null;
}

interface ContentProps {
  loading?: boolean;
  error?: string;
  selectedTabIndex?: number;
  onSelectTab?: (title: string, tabIndex: number) => void;
  tabs: TabProps[];
}

interface ScreenProps {
  header: HeaderProps;
  content: ContentProps;
}

const makeHeader = (props: HeaderProps) => {
  return (
    <>
      <SelectContainer>
        <DatePickerHeader
          summary={<ComparedToContainer>{props.comparedTo}</ComparedToContainer>}
          onChange={props.datePicker.onChange}
          currentPreset={props.datePicker.currentPreset}
          dateRangePresets={props.datePicker.dateRangePresets}
          startDate={props.datePicker.startDate}
          endDate={props.datePicker.endDate}
          useSimplePanel={true}
        />
        {makeReferenceHeader(props.referenceComboProps)}
        <LeftSpacer />
        {props.referenceContext ? (
          <Container style={{ minWidth: "200px" }}> {props.referenceContext} </Container>
        ) : null}
      </SelectContainer>
    </>
  );
};

const makeContent = (props: ContentProps) => {
  return (
    <>
      {props.error ? <Callout variant="critical" description={props.error} /> : null}
      {props.loading ? <Spinner variant="monochrome" /> : null}
      {!props.error && !props.loading ? (
        <>
          <DataGroupContainer>
            <Tabs
              tabs={props.tabs.map((tab) => {
                return {
                  title: tab.title,
                  content: <Tab {...tab} />,
                  disabled: false,
                };
              })}
              selectedTabIndex={props.selectedTabIndex}
              onSelectTab={props.onSelectTab}
            />
          </DataGroupContainer>
        </>
      ) : null}
    </>
  );
};

const Screen = ({ header, content }: ScreenProps) => {
  return (
    <AppShell>
      <FullWidthPage title={header.title}>
        {makeHeader(header)}
        {makeContent(content)}
      </FullWidthPage>
    </AppShell>
  );
};

/**
 * Entry point
 */

const PaidChannelsSummary = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath, notesPath } = getUrls(customerDomain);
  const [dataError, setDataError] = React.useState<string | null>(null);
  const isStaff = useAnalyticsContext().isStaff;
  const notesValue = React.useRef<string>("");

  const { selectedTimeRange, handleTimeRangeChange, currentPreset } = useDateAndTemp();

  const onFetchError = React.useCallback((ex) => {
    setDataError(ex);
  }, []);

  const { referencePeriod, setReferencePeriod } = useReferencePeriod();

  React.useEffect(() => {
    getNotes(notesPath, "paid-channels", (result: any) => {
      notesValue.current = result.content;
    });
  }, [notesPath]);

  let tabs: TabProps[] = [];

  const groupedTilesProps = useGroupedTiles(
    selectedTimeRange,
    dashboardDataPath,
    currentPreset,
    onFetchError,
    referencePeriod
  );
  tabs = addGroupedTiles(groupedTilesProps, tabs);

  // Graphs need thought before wider release
  // const graphProps = useGraphs(selectedTimeRange, dashboardDataPath, currentPreset, onFetchError, hideWeekly, hideMonthly, selectedButtonIndex, handleTempResChange, temporalResolution, referencePeriod);
  // if (graphProps) {
  //   tabs = addGraphs(graphProps.props, tabs);
  // }

  // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  tabs = addNotes(isStaff ?? false, notesValue, notesPath, "paid-channels", tabs);

  const props: ScreenProps = {
    header: {
      title: insightsIntl.formatMessage({ id: "Paid Channels Summary", defaultMessage: "Paid Channels Summary" }),
      comparedTo: insightsIntl.formatMessage({ id: "compared to", defaultMessage: "compared to" }),
      datePicker: {
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleTimeRangeChange(dateRangePreset as Parameters<typeof handleTimeRangeChange>[0], startDate, endDate),
      },
      referenceComboProps: makeReferenceProps(referencePeriod, setReferencePeriod),
      referenceContext: makeAdditionalReferenceContext(referencePeriod, selectedTimeRange),
    },
    content: {
      loading: true,
      tabs: tabs,
    },
  };

  if (dataError) {
    props.content.loading = false;
    props.content.error = dataError;
  } else {
    props.content.loading = false;
  }

  return <Screen {...props} />;
};

export default PaidChannelsSummary;
